// IsValid.js
const MIN_USER_ID_LEN = 3;
const MAX_USER_ID_LEN = 30;
//const USER_ID_PATTERN = /^[_a-zA-Z0-9]{4,100}$/;
const YMD_PATTERN = /^(19|20)\d\d-(0[1-9]|1[012])-[0-3]\d$/;
const YMD_HMS_PATTERN = /^(19|20)\d\d-(0[1-9]|1[012])-[0-3]\d [0-2]\d:[0-5]\d:[0-5]\d$/;
//const WORD_CODE_PATTERN = /^\w+$/;
//const NON_SPACE_CODE_PATTERN = /^[-\.@#\w]+$/;
const NUMERIC_PATTERN = /^\d+$/;
const MOBILE_PATTERN = /^01\d{8,9}$/;
const HEX_21TO7E_PATTERN = /^[\x21-\x7E]+$/;
const SPACE_PATTERN = /\s/;
const PW_UPPER_CASE = /[A-Z]/;
const PW_LOWER_CASE = /[a-z]/;
const PW_NUMBER = /[0-9]/;
const PW_PUNCT = /[-~!@#$%^&*()_+=[\]|\\;:‘“<>,?/.]/;
const EMAIL_PATTERN = /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,10}$/;
const PACKAGE_PATTERN = /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*$/;
const ZIPCODE_PATTERN = /^\d{5}$/;
const HHMM4_PATTERN = /^([0-1]\d|2[0-3])[0-5]\d$/;
const HH_COLON_MM_PATTERN = /^([0-1]\d|2[0-3]):[0-5]\d$/;

export default class IsValid {
    static nullString(str) {
        return str === undefined || str === null || str === '';
    }

    static getUtf8BytesLength(text) {
        const textLength = text.length;
        let length = 0;
        let index = 0;
        const maxLength = 300;
        const encoder = new TextEncoder();
        
        while (index < textLength) {
            const chunk = encoder.encode(text.substring(index, index + maxLength));
            const chunkLength = chunk ? chunk.length : 0;
            length += chunkLength;
            index += maxLength;
        }
        
        return length;
    }

    static text(str) {
        if(str) return IsValid.getUtf8BytesLength(str.trim()) > 0;
        return false;
    }

    static string(str, min, max) {
        if(min <= 0 && IsValid.nullString(str)) return true;
        const len = IsValid.getUtf8BytesLength(str.length < 1000 ? str.trim() : str);
        return len >= min && len <= max;
    }
    
    static dcCode(str) {
        return /^9\d\d$/.test(str);
    }

    static numericString(str, min, max) {
        if(min <= 0 && IsValid.nullString(str)) return true;
        const len = IsValid.getUtf8BytesLength(str);
        return len >= min && len <= max && NUMERIC_PATTERN.test(str);
    }

    static email(str, maxLen) {
        if(IsValid.nullString(str)) return true;
        let max = maxLen > 0 ? maxLen : MAX_USER_ID_LEN;
        return str.length <= max && EMAIL_PATTERN.test(str);
    }

    static emailNullable(str, maxLen) {
        if(IsValid.nullString(str)) return true;
        let max = maxLen > 0 ? maxLen : MAX_USER_ID_LEN;
        return str.length <= max && EMAIL_PATTERN.test(str);
    }

    static regex(str, pattern) {
        if(IsValid.nullString(str)) return false;
        return pattern.test(str);
    }

    static package(str, maxLen) {
        if(IsValid.nullString(str)) return false;
        return str.length <= maxLen && PACKAGE_PATTERN.test(str);
    }

    static packageNullable(str, maxLen) {
        if(IsValid.nullString(str)) return true;
        return str.length <= maxLen && PACKAGE_PATTERN.test(str);
    }

    static userId(str) {
        if(IsValid.nullString(str)) return false;
        return str.length >= MIN_USER_ID_LEN && str.length <= MAX_USER_ID_LEN && PACKAGE_PATTERN.test(str);
    }

    static password(value) {
        if(IsValid.nullString(value)) return false;
        if(value.length >= 1 && !SPACE_PATTERN.test(value)) {
            let chk = 0;
            
            if(/\w/.test(value)) {chk++;}
            if(PW_NUMBER.test(value)) {chk++;}
            if(PW_PUNCT.test(value)) { chk++; }

            return chk > 1;
        }
        return false;
    }

    static passwordStrict(value) {
        if(IsValid.nullString(value)) return false;
        if(value.length >= 1 && !SPACE_PATTERN.test(value)) {
            let chk = 0;
            
            if(/\w/.test(value)) {chk++;}
            if(PW_NUMBER.test(value)) {chk++;}
            if(PW_PUNCT.test(value)) { chk++; }

            return chk > 1;
        }
        return false;
    }

    static passwordNullable(value) {
        if(IsValid.nullString(value)) return true;
        return IsValid.password(value);
    }

    static passwordOldSite(value) {
        if(IsValid.nullString(value)) return false;
        return value.length >= 1 && value.length <= 20;
    }

    static naivePassword(value, min) {
        if(IsValid.nullString(value)) return false;
        const minLen = min ? min : 2;
        if(value.length >= minLen && HEX_21TO7E_PATTERN.test(value)) {
            return true;
        }
        return false;
    }

    static koreanPassword(value) {
        if(IsValid.nullString(value)) return false;
        if(value.length >= 8) {
            let chk = 0;

            if(PW_UPPER_CASE.test(value)) {chk++;}
            if(PW_LOWER_CASE.test(value)) {chk++;}
            if(PW_NUMBER.test(value)) {chk++;}
            if(PW_PUNCT.test(value)) {chk++;}

            return chk >= 3;
        }
        return false;
    }

    static koreanPasswordNullable(value) {
        if(IsValid.nullString(value)) return true;
        return IsValid.koreanPassword(value);
    }

    static mobile(value) {
        if(IsValid.nullString(value)) return false;
        const digits = value.replace(/\D/g, '');
        return MOBILE_PATTERN.test(digits);
    }

    static date(value) {
        if(IsValid.nullString(value)) return false;
        return YMD_PATTERN.test(value);
    }

    static datetime(value) {
        if(IsValid.nullString(value)) return false;
        return YMD_HMS_PATTERN.test(value);
    }

    static dateNullable(value) {
        if(IsValid.nullString(value)) return true;
        return IsValid.date(value);
    }

    static datetimeNullable(value) {
        if(IsValid.nullString(value)) return true;
        return IsValid.datetime(value);
    }

    static identical(one, two) {
        if(one === two) return true;
        if(!one || !two) return false;
        if(one.length === two.length) {
            return one.every((v, i) => v === two[i]);
        }
        return false;
    }

    static belongTo(text, ...strings) {
        if(text) {
            const t = text.trim();
            if(t.length > 0) {
                const parts = t.split(/\s+/);
                return parts.some(p => strings.includes(p));
            }
        }
        return false;
    }

    static indexOfAny(text, ...strings) {
        if(text) {
            const t = text.trim();
            if(t.length > 0) {
                const parts = t.split(/\s+/);
                for(const p of parts) {
                    for(const s of strings) {
                        if(s.indexOf(p) >=0) return true;
                    }
                }
            }
        }
        return false;
    }

    static isValidDate(year, monthIndex, day) {
        const date = new Date(year, monthIndex, day);
        return date && (date.getMonth() === monthIndex) && (date.getDate() === Number(day));
    }

    static zipCode(zipcode) {
        return ZIPCODE_PATTERN.test(zipcode);
    }

    static hhmm4(hhmm) {
        return HHMM4_PATTERN.test(hhmm);
    }

    static hhColonMm(hhmm) {
        return HH_COLON_MM_PATTERN.test(hhmm);
    }
};